'use client'

import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useState } from 'react'

export function DeletionPendingBanner({
  scheduledDate,
  daysRemaining,
  canCancel,
}: {
  scheduledDate: string
  daysRemaining: number
  canCancel: boolean
}) {
  const router = useRouter()
  const [cancelling, setCancelling] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (dismissed) return null

  const cancel = async () => {
    setError(null)
    setCancelling(true)
    try {
      const res = await fetch('/api/v1/gdpr/delete-account', { method: 'DELETE' })
      if (res.ok) {
        setDismissed(true)
        router.refresh()
        return
      }
      // Surface the failure instead of swallowing it - given the stakes
      // (missed cancellation → permanent deletion), silence is unsafe.
      const body = (await res.json().catch(() => ({}))) as { error?: string }
      setError(body.error ?? `Cancel failed (HTTP ${res.status})`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Network error')
    } finally {
      setCancelling(false)
    }
  }

  return (
    <div
      role="alert"
      className="w-full"
      style={{
        background: 'var(--color-semantic-error)',
        color: 'white',
      }}
    >
      <div className="max-w-6xl mx-auto py-2 px-4 flex flex-wrap items-center justify-between gap-2 text-sm">
        <div className="min-w-0">
          <p>
            Your account is scheduled for deletion on{' '}
            <strong>{new Date(scheduledDate).toLocaleDateString()}</strong> (
            {daysRemaining} day{daysRemaining === 1 ? '' : 's'} remaining).
          </p>
          {error && (
            <p className="mt-0.5 text-xs" style={{ opacity: 0.9 }}>
              {error}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canCancel && (
            <button
              type="button"
              onClick={cancel}
              disabled={cancelling}
              className="rounded-md px-3 py-1 text-sm font-medium"
              style={{
                background: 'rgba(255,255,255,0.2)',
                color: 'white',
              }}
            >
              {cancelling ? 'Cancelling…' : 'Cancel deletion'}
            </button>
          )}
          <Link
            href="/settings/privacy"
            className="rounded-md px-3 py-1 text-sm font-medium underline"
          >
            Manage
          </Link>
        </div>
      </div>
    </div>
  )
}
